<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePeopleStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

/**
 * Define the people store instance and references.
 */
const peopleStore = usePeopleStore();
const { totals } = storeToRefs(peopleStore);
const { getTotals } = peopleStore;

/**
 * Define a route instance.
 */
const route = useRoute();
/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Only display the user totals within the user list.
 */
const showTotals = computed<boolean>(
  () =>
    (route.name === 'user.list' || route.name === 'user.referrals') &&
    can([PERMISSIONS.PEOPLE_TOTALS, PERMISSIONS.PEOPLE_INDEX_CONNECTED]),
);
const isUserMainPage = computed<boolean>(() => route.name === 'user.list');

/**
 * Define the total of people available.
 */
const total = computed<number>(() => totals.value.data.total);

/**
 * Define the total or active people.
 */
const active = computed<number>(() => totals.value.data.active);

/**
 * Initialize the people listing and the available types.
 */
const init = async () => {
  if (can([PERMISSIONS.PEOPLE_TOTALS, PERMISSIONS.PEOPLE_INDEX_CONNECTED])) await getTotals();
};
</script>

<template>
  <div v-if="showTotals" class="flex align-items-center justify-content-center text-black h-2rem mt-2 select-none">
    <div class="left col-fixed p-2 bg-white">
      <span class="mr-1 text-xs font-normal">Total</span>
      <span class="text-sm font-bold" v-text="total" />
    </div>
    <div v-if="isUserMainPage" class="right col-fixed p-2 bg-white">
      <span class="mr-1 text-xs font-normal">Active</span>
      <span class="text-sm font-bold" v-text="active" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.flex {
  .left {
    -webkit-border-radius: 6px 0 0 6px;
    -moz-border-radius: 6px 0 0 6px;
    border-radius: 6px 0 0 6px;
  }

  .right {
    -webkit-border-radius: 0 6px 6px 0;
    -moz-border-radius: 0 6px 6px 0;
    border-radius: 0 6px 6px 0;
  }

  .col-fixed {
    margin-left: 1px;
  }
}
</style>
